import {
  Sun,
  Cloud,
  CloudRain,
  CloudSnow,
  CloudDrizzle,
  CloudFog,
  CloudLightning,
  Wind,
} from 'lucide-react';
import type { TFunction } from 'i18next';

export function getWeatherIcon(icon: string) {
  const icons: Record<string, any> = {
    sun: Sun,
    'cloud-sun': Cloud,
    cloud: Cloud,
    'cloud-rain': CloudRain,
    'cloud-rain-heavy': CloudRain,
    'cloud-drizzle': CloudDrizzle,
    'cloud-snow': CloudSnow,
    'cloud-fog': CloudFog,
    'cloud-lightning': CloudLightning,
    'cloud-hail': CloudSnow,
    wind: Wind,
  };

  return icons[icon] || Cloud;
}

export function translateWeatherDescription(description: string, t: TFunction): string {
  if (!description) return description;
  
  // Map common weather API responses to translation keys
  const descriptionMap: Record<string, string> = {
    'Clear': 'clear',
    'Mostly Clear': 'clear',
    'Cloudy': 'cloudy',
    'Partly Cloudy': 'partly_cloudy',
    'Partly cloudy': 'partly_cloudy',
    'Mostly Cloudy': 'cloudy',
    'Overcast': 'overcast',
    'Fog': 'fog',
    'Light Fog': 'fog',
    'Light fog': 'fog',
    'Mist': 'mist',
    'Rain': 'rain',
    'Light rain': 'light_rain',
    'Heavy rain': 'heavy_rain',
    'Showers': 'showers',
    'Snow': 'snow',
    'Light snow': 'light_snow',
    'Heavy snow': 'heavy_snow',
    'Sleet': 'sleet',
    'Hail': 'hail',
    'Thunderstorm': 'thunderstorm',
    'Windy': 'windy',
    'Hot': 'hot',
    'Cold': 'cold',
    'Drizzle': 'drizzle',
  };
  
  // Try direct map first
  const mappedKey = descriptionMap[description];
  if (mappedKey) {
    return t(`weatherConditions.${mappedKey}`, description);
  }
  
  // Fallback: convert to key format (lowercase with underscores)
  const key = description?.toLowerCase().replace(/\s+/g, '_');
  if (!key) return description;
  const translated = t(`weatherConditions.${key}`, description);
  return translated || description;
}

export function getWeatherGradient(weatherCode: number): string {
  // Clear/Sunny
  if (weatherCode === 1000 || weatherCode === 1100) {
    return 'gradient-sunny';
  }

  // Cloudy
  if ([1101, 1102, 1001].includes(weatherCode)) {
    return 'gradient-cloudy';
  }

  // Rain
  if ([4000, 4001, 4200, 4201].includes(weatherCode)) {
    return 'gradient-rainy';
  }

  // Snow
  if ([5000, 5001, 5100, 5101].includes(weatherCode)) {
    return 'bg-gradient-to-br from-blue-200 to-blue-400';
  }

  // Thunderstorm
  if (weatherCode === 8000) {
    return 'bg-gradient-to-br from-gray-700 to-gray-900';
  }

  // Default
  return 'gradient-cloudy';
}

export function getTemperatureColor(temp: number): string {
  if (temp >= 30) return 'text-red-600';
  if (temp >= 25) return 'text-orange-500';
  if (temp >= 20) return 'text-yellow-500';
  if (temp >= 15) return 'text-green-500';
  if (temp >= 10) return 'text-blue-500';
  if (temp >= 0) return 'text-cyan-500';
  return 'text-indigo-600';
}

export function getUVIndexLevel(uvIndex: number): { level: string; color: string } {
  if (uvIndex <= 2) return { level: 'Niedrig', color: 'text-green-600' };
  if (uvIndex <= 5) return { level: 'Mäßig', color: 'text-yellow-600' };
  if (uvIndex <= 7) return { level: 'Hoch', color: 'text-orange-600' };
  if (uvIndex <= 10) return { level: 'Sehr hoch', color: 'text-red-600' };
  return { level: 'Extrem', color: 'text-purple-600' };
}

export function getWindDirection(degrees: number): string {
  const directions = ['N', 'NNO', 'NO', 'ONO', 'O', 'OSO', 'SO', 'SSO', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const index = Math.round(degrees / 22.5) % 16;
  return directions[index];
}
